/**
 * Featured Collections slot keys — system collections (Loom) + creator collections.
 *
 * System:
 *   sys:trending
 *   sys:brand:<brandSlug>
 *   sys:brands:<slug1>+<slug2>+…
 * Creator / own:
 *   any other key (carousel id or slug)
 */

export const SYS_TRENDING = "sys:trending";
export const SYS_BRAND_PREFIX = "sys:brand:";
export const SYS_BRANDS_PREFIX = "sys:brands:";
/** Cap for Featured brands collection tiles (Loom). */
const FEATURED_BRANDS_SLOT_MAX = 4;

export type FeaturedCollectionSlotKind =
  | "trending"
  | "shop-brand"
  | "featured-brands"
  | "collection";

export function collectionSlotKind(key: string): FeaturedCollectionSlotKind {
  const k = String(key || "").trim();
  if (k === SYS_TRENDING || k === "trending") return "trending";
  if (k.startsWith(SYS_BRAND_PREFIX) || k.startsWith("brand:")) return "shop-brand";
  if (k.startsWith(SYS_BRANDS_PREFIX) || k.startsWith("brands:")) {
    return "featured-brands";
  }
  return "collection";
}

export function makeTrendingKey(): string {
  return SYS_TRENDING;
}

export function makeShopBrandKey(brandSlug: string): string {
  const slug = String(brandSlug || "").trim().toLowerCase();
  return slug ? `${SYS_BRAND_PREFIX}${slug}` : SYS_BRAND_PREFIX;
}

export function makeFeaturedBrandsKey(slugs: string[]): string {
  const clean = slugs
    .map((s) => String(s || "").trim().toLowerCase())
    .filter(Boolean)
    .slice(0, FEATURED_BRANDS_SLOT_MAX);
  return `${SYS_BRANDS_PREFIX}${clean.join("+")}`;
}

export function parseShopBrandSlug(key: string): string {
  const k = String(key || "").trim();
  if (k.startsWith(SYS_BRAND_PREFIX)) return k.slice(SYS_BRAND_PREFIX.length);
  if (k.startsWith("brand:")) return k.slice("brand:".length);
  return "";
}

export function parseFeaturedBrandSlugs(key: string): string[] {
  const k = String(key || "").trim();
  let rest = "";
  if (k.startsWith(SYS_BRANDS_PREFIX)) rest = k.slice(SYS_BRANDS_PREFIX.length);
  else if (k.startsWith("brands:")) rest = k.slice("brands:".length);
  else return [];
  return rest
    .split(/[+|,]/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
    .slice(0, FEATURED_BRANDS_SLOT_MAX);
}

/** Brand slugs referenced by featured-collection system keys (for cover fetches). */
export function brandSlugsFromCollectionKeys(keys: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const key of keys) {
    const kind = collectionSlotKind(key);
    if (kind === "shop-brand") {
      const slug = parseShopBrandSlug(key);
      if (slug && !seen.has(slug)) {
        seen.add(slug);
        out.push(slug);
      }
    } else if (kind === "featured-brands") {
      for (const slug of parseFeaturedBrandSlugs(key)) {
        if (!seen.has(slug)) {
          seen.add(slug);
          out.push(slug);
        }
      }
    }
  }
  return out;
}

export function isSystemCollectionKey(key: string): boolean {
  return collectionSlotKind(key) !== "collection";
}

export function systemCollectionLabel(
  key: string,
  brandLabelForSlug?: (slug: string) => string,
): string {
  const kind = collectionSlotKind(key);
  if (kind === "trending") return "Trending products";
  if (kind === "shop-brand") {
    const slug = parseShopBrandSlug(key);
    const label = brandLabelForSlug?.(slug) || slug;
    return label ? `Shop ${label}` : "Shop a brand";
  }
  if (kind === "featured-brands") {
    const slugs = parseFeaturedBrandSlugs(key);
    if (!slugs.length) return "Featured brands";
    const labels = slugs.map((s) => brandLabelForSlug?.(s) || s);
    return labels.join(", ");
  }
  return "Collection";
}
